import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { faAngleDown, faCircleCheck, faCirclePause, faLock, faMagnifyingGlass, faKey } from '@fortawesome/free-solid-svg-icons';

import AccessStatusCard from '../access/AccessStatusCard.jsx';
import RequestGameAccessModal from '../../pages/admin/access-management/game-access-request/RequestGameAccessModal.jsx';
import { updateDashboardContext } from '../../redux-store/ReduxStore.js';
import './NoGameAccessPlaceholder.css';

/**
 * Shown by <RequireGameAccess> in place of a game page whose game the user cannot open (see routeGameAccess.js):
 * a game of this org they hold no access to - named, with the same "Request access" the Games page offers - or a
 * game that does not exist here. Before this, the page rendered anyway and every panel failed on its own with
 * "Insufficient permissions".
 */
const RouteGameAccessCard = ({ state, game, slug }) => {
    const dispatch = useDispatch();
    const [requesting, setRequesting] = useState(false);
    const [requested, setRequested] = useState(false);
    const openGameSwitcher = () => dispatch(updateDashboardContext({ openContextSwitcherRequest: true }));

    if (state === 'unknown') {
        return (
            <div className="no-game-access">
                <AccessStatusCard
                    tone="neutral"
                    icon={faMagnifyingGlass}
                    title="Game not found"
                    message={`No game called “${slug}” exists in this organization. Pick one of your games from the game switcher.`}
                    action={{ label: 'Open game switcher', icon: faAngleDown, variant: 'secondary', onClick: openGameSwitcher }}
                />
            </div>
        );
    }

    const name = game?.gameName || 'this game';
    // Suspended by an owner, on this very game: said plainly, and no request - an owner took the access away on
    // purpose and is the one to give it back.
    if (state === 'suspended') {
        return (
            <div className="no-game-access">
                <AccessStatusCard
                    tone="warning"
                    icon={faCirclePause}
                    title={`Your access to ${name} is suspended`}
                    message={`An owner of ${name} suspended your access. Your role is kept: ask one of the game's owners to `
                        + 'reactivate it. Your other games are not affected.'}
                    action={{ label: 'Open game switcher', icon: faAngleDown, variant: 'secondary', onClick: openGameSwitcher }}
                />
            </div>
        );
    }
    if (requested) {
        return (
            <div className="no-game-access">
                <AccessStatusCard
                    tone="success"
                    icon={faCircleCheck}
                    title="Request sent"
                    message={`Your request for ${name} is with its owner. You will get an email and a notification once it is approved or declined.`}
                    action={{ label: 'Open game switcher', icon: faAngleDown, variant: 'secondary', onClick: openGameSwitcher }}
                />
            </div>
        );
    }
    return (
        <div className="no-game-access">
            <AccessStatusCard
                tone="neutral"
                icon={faLock}
                title={`You don't have access to ${name}`}
                message={`Your access in this organization does not include ${name}. Request it and the game's owner will review the request, or switch to one of your games.`}
                action={{ label: 'Request access', icon: faKey, onClick: () => setRequesting(true) }}
            />
            <RequestGameAccessModal
                isOpen={requesting}
                game={game}
                onClose={() => setRequesting(false)}
                onSubmitted={() => { setRequesting(false); setRequested(true); }}
            />
        </div>
    );
};

export default RouteGameAccessCard;
